/**
 * viewer.js - Canvas-backed document viewer with two render modes:
 * 1. PDF.js for raw PDFs
 * 2. Progressive rendered thumbnails, previews, and viewport tiles for heavy plans
 */

import * as pdfjsLib from 'pdfjs-dist'
import { TextLayer } from 'pdfjs-dist'
import 'pdfjs-dist/web/pdf_viewer.css'
import PdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { documentPdfUrl } from './api.js'
import { getToken } from './auth.js'

pdfjsLib.GlobalWorkerOptions.workerSrc = PdfjsWorkerUrl

/**
 * Render a File's first page to a JPEG data URL — used for the ingest chat card
 * and the plan-vs-spec vision classification. Fast: only page 1, capped width.
 * @param {File} file
 * @param {number} maxW  max width in px
 * @param {number} quality  JPEG quality 0-1
 * @returns {Promise<string>} data URL (image/jpeg)
 */
export async function renderFileFirstPageThumb(file, maxW = 900, quality = 0.82) {
  const data = new Uint8Array(await file.arrayBuffer())
  const pdf = await pdfjsLib.getDocument({ data }).promise
  try {
    const page = await pdf.getPage(1)
    const vp1 = page.getViewport({ scale: 1 })
    const scale = Math.min(maxW / vp1.width, 2)
    const vp = page.getViewport({ scale })
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(vp.width)
    canvas.height = Math.round(vp.height)
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: ctx, viewport: vp }).promise
    return canvas.toDataURL('image/jpeg', quality)
  } finally {
    try { await pdf.destroy() } catch { /* ignore */ }
  }
}

const MIN_SCALE = 0.1
const MAX_SCALE = 5.0
const ZOOM_STEP = 1.25
const WHEEL_BASE = 1.15
const RENDER_QUALITY = 4
const MAX_CANVAS_DIM = 16384
const MAX_CANVAS_PIXELS = 268_435_456
const MAX_RENDERED_CANVAS_DIM = 12000
const MAX_RENDERED_CANVAS_PIXELS = 80_000_000
const MAX_TILE_IMAGE_CACHE_ITEMS = 240
const MAX_PREVIEW_IMAGE_CACHE_ITEMS = 24
const MIN_PREVIEW_REQUEST_WIDTH = 320
const MAX_PREVIEW_REQUEST_WIDTH = 8000
// Supersample factor for the fit-view preview. The canvas BACKING store is kept at the
// display's device-pixel size and shown 1:1 (so the browser never downscales the canvas
// element — that path uses a cheap bilinear filter that muddies text). The preview is
// fetched at OVERSAMPLE x device px and downscaled into the backing via drawImage with
// imageSmoothingQuality='high' for clean, area-averaged anti-aliasing.
const FIT_PREVIEW_OVERSAMPLE = 3.0
const MIN_PREVIEW_EFFECTIVE_RATIO = 0.9
const TILE_IDLE_MS = 45
// Unsharp-mask strength applied to the downscaled preview. When a high-res preview is
// area-averaged down to fit resolution, thin black linework averages toward gray and
// looks faded/blurry. A light sharpen pass restores local contrast so lines snap back
// to black. 0 = off. Live-tunable at runtime via localStorage 'viewerSharpen' (e.g.
// localStorage.setItem('viewerSharpen','0.8')) so strength can be dialed without a redeploy.
const PREVIEW_SHARPEN_AMOUNT = 0.6
// Text stem-darkening. pdf.js anti-aliases text with LIGHT gray edges, so at fit (~40 px/in on
// a small screen) thin strokes fade to gray and look washed-out next to Acrobat's hinted, solid
// black stems. A gamma curve (<1 darkens midtones) + slight black/white point snap pulls those
// gray AA pixels back toward black so text reads bold and crisp. 1 = off. Live-tunable via
// localStorage 'viewerTextContrast' (e.g. '0.6' bolder, '0.85' subtle, '1' off).
const PREVIEW_TEXT_GAMMA = 0.72
// Skip sharpening above this backing-store pixel count so the getImageData/putImageData
// readback never hitches during high-zoom renders. Kept generous so normal fit-view plan
// sheets (incl. high-DPI / wide viewers, easily 8-16M px) still get the line-darkening +
// unsharp pass that pulls pdf.js's light-gray AA strokes back to solid black; only extreme
// zoomed-in canvases exceed it and fall back to the plain (already dense enough) render.
const PREVIEW_SHARPEN_MAX_PIXELS = 24_000_000
// Feature B — Chrome/PDFium-style client-vector render. Rendered-mode pages are drawn from
// the REAL vector PDF via pdf.js (supersampled) as the crisp layer at fit AND moderate zoom,
// exactly like the native Chrome PDF viewer — no lossy WebP, no CSS-downscaled tile mosaic.
// The WebP preview stays as the instant placeholder; the vector render swaps in when ready.
// Tiles are used only as a fallback: at EXTREME zoom (full-page canvas too big to render every
// frame) or for pathologically dense pages above CLIENT_VECTOR_MAX_OPS. Live-tunable via
// localStorage 'viewerVectorOps' (op ceiling; '0' disables and always uses tiles).
const CLIENT_VECTOR_MAX_OPS = 1_200_000
// Supersample factor: render the vector page at up to SS x the display-canvas resolution into
// an offscreen buffer, then high-quality downscale into the visible canvas. Gives crisp AA
// linework at fit (device-res alone looked soft). Live-tunable via localStorage 'viewerVectorSS'.
const CLIENT_VECTOR_SS = 2
// Cap the offscreen buffer's longest side (px). Doubles as the vector/tile handoff threshold:
// when the display canvas itself exceeds this (extreme zoom / huge sheet) we hand off to the
// windowed tile mosaic, which is far cheaper than re-rendering the whole page every frame.
const CLIENT_VECTOR_MAX_DIM = 4600

let _pdfCanvas = null
let _textLayerDiv = null
let _wrapper = null
let _container = null
let _scroll = null
let _tileLayer = null
let _ctx = null
let _initialised = false

let _mode = 'pdf'
let _pdf = null
let _page = null
let _renderedManifest = null
let _currentPageIdx = 0
let _navSerial = 0
let _lastSource = null
let _scale = 1.0
let _fitScale = 1.0
let _renderTask = null
let _renderSerial = 0
let _userZoom = 1.0

let _zoomMode = false
let _textSelectMode = false
let _toolActive = false
let _pageFilter = null
let _panStart = null
let _renderTimeout = null
let _textLayerInst = null

let _renderScale = 1.0
let _renderCssW = 0
let _renderCssH = 0
let _renderPageIdx = -1
let _sourceObjectUrl = null

let _gestureActive = false
let _gestureAnchor = null

let _resizeTimeout = null
let _prefetchTimer = null
let _prefetchSerial = 0
let _tileTimer = null
let _tileSerial = 0
let _tileLayerKey = ''
let _fitTilesNeeded = false
let _lastPreviewDiagnostics = null

const _imageCache = new Map()
const _imageObjectUrls = new Map()
const _pdfUrlDocCache = new Map()
// Background pdf.js document used ONLY to pull the original vector text of the current
// page in rendered mode, so labels/dimensions/titles stay razor-sharp at any zoom while
// the linework is served by the raster preview + tiles. Loaded lazily via range requests.
let _textPdfPromise = null
let _textPdfDocId = null
// Feature B state: per-page vector-complexity classification ('light' | 'dense'),
// keyed `${docId}:${pageIdx}`; whether the current page is showing a crisp client-vector
// render (tiles suppressed while true); and the in-flight client-vector render task.
const _pageVectorClass = new Map()
// Cached getTextContent per page so zoom re-renders reuse the parse for the text-crispen pass.
const _textContentCache = new Map()
// Smooth darkening curves (gamma > 1): white bg untouched, mid/dark glyph pixels pushed
// toward black progressively so the AA gradient survives (no blocky edges). Cached per gamma.
const _darkenLutCache = new Map()
function _darkenLutFor(gamma) {
  let lut = _darkenLutCache.get(gamma)
  if (!lut) {
    lut = new Uint8ClampedArray(256)
    for (let v = 0; v < 256; v++) lut[v] = Math.round(255 * Math.pow(v / 255, gamma))
    _darkenLutCache.set(gamma, lut)
  }
  return lut
}
let _clientVectorActive = false
let _clientVectorTask = null
const _emitter = new EventTarget()

export function init() {
  if (_initialised) return
  _pdfCanvas = document.getElementById('pdf-canvas')
  _textLayerDiv = document.getElementById('pdf-text-layer')
  _wrapper = document.getElementById('page-wrapper')
  _container = document.getElementById('page-container')
  _scroll = document.getElementById('scroll-container')
  _ctx = _pdfCanvas.getContext('2d')
  _ctx.imageSmoothingEnabled = true
  _ctx.imageSmoothingQuality = 'high'
  _initialised = true

  _scroll.addEventListener('wheel', _onWheel, { passive: false })
  _scroll.addEventListener('scroll', _onScroll, { passive: true })
  _scroll.addEventListener('mousedown', _onMouseDown)
  window.addEventListener('mousemove', _onMouseMove)
  window.addEventListener('mouseup', _onMouseUp)
  window.addEventListener('resize', _onResize)
}

function _ensureTileLayer() {
  if (!_wrapper || !_pdfCanvas) return null
  if (_tileLayer && _tileLayer.parentElement === _wrapper) return _tileLayer
  _tileLayer = document.createElement('div')
  _tileLayer.className = 'render-tile-layer'
  _wrapper.insertBefore(_tileLayer, _textLayerDiv || _pdfCanvas.nextSibling)
  return _tileLayer
}

export async function loadPdf(source) {
  _resetDocumentState()
  _mode = 'pdf'
  _lastSource = source

  let task
  if (source instanceof File) {
    _sourceObjectUrl = URL.createObjectURL(source)
    task = pdfjsLib.getDocument(_sourceObjectUrl)
  } else {
    task = pdfjsLib.getDocument(source)
  }

  _pdf = await task.promise
  _currentPageIdx = 0
  _page = await _pdf.getPage(1)
  await _openCurrentPage()
  return _pdf
}

export async function loadRendered(manifest) {
  _resetDocumentState()
  // Cached plans now display as PURE single-page vector via pdf.js — same viewer as
  // small files (fit-to-page + zoom), no WebP preview / tile mosaic / sharpen. The PDF
  // is streamed via HTTP range requests (Clerk bearer token) so big files don't fully
  // download. The manifest is kept only for page metadata (callouts, page count).
  _mode = 'pdf'
  _renderedManifest = manifest
  const docId = manifest?.doc_id
  const token = docId ? await getToken() : null
  const params = { url: documentPdfUrl(docId) }
  if (token) params.httpHeaders = { Authorization: `Bearer ${token}` }
  _pdf = await pdfjsLib.getDocument(params).promise
  _currentPageIdx = 0
  _page = await _pdf.getPage(1)
  await _openCurrentPage()
  return _pdf
}

export async function openShared(pdf, pageIdx = 0) {
  _resetDocumentState()
  _mode = 'pdf'
  _pdf = pdf
  _currentPageIdx = Math.max(0, Math.min(pageIdx, pdf.numPages - 1))
  _page = await _pdf.getPage(_currentPageIdx + 1)
  await _openCurrentPage()
  return _pdf
}

export async function createThumbDoc() {
  if (_mode !== 'pdf') return null
  const source = _lastSource
  if (!source) return null
  try {
    const url = URL.createObjectURL(source)
    const task = pdfjsLib.getDocument(url)
    task.promise.finally(() => URL.revokeObjectURL(url))
    return await task.promise
  } catch {
    return null
  }
}

export const getPdf = () => _pdf
export const getFile = () => _lastSource
export const pageCount = () => (_pdf ? _pdf.numPages : 0)
export const currentPage = () => _currentPageIdx
export const currentScale = () => _scale
export const getFitScale = () => _fitScale
export const isZoomMode = () => _zoomMode
export const getUserZoom = () => _userZoom
export const isRenderedMode = () => _mode === 'rendered'
export const getRenderedManifest = () => _renderedManifest

export function setTextSelectMode(on) {
  _textSelectMode = on
  if (_scroll) _scroll.classList.toggle('text-select-active', on)
}

export function setToolActive(on) {
  _toolActive = on
}

// Defensive reset of transient interaction state. Called when leaving a tool
// mode (e.g. text-select) so a half-finished pan/zoom gesture or a lingering
// DOM text selection can never leave the viewer feeling "locked".
export function resetInteraction() {
  _clearGestureState()
  if (_scroll) _scroll.classList.remove('panning')
  try { window.getSelection()?.removeAllRanges() } catch { /* ignore */ }
}

// Live interaction-state probe for diagnosing "stuck/locked" reports from the
// console: window.__viewerDebug().
if (typeof window !== 'undefined') {
  window.__viewerDebug = () => ({
    mode: _mode,
    zoomMode: _zoomMode,
    toolActive: _toolActive,
    textSelectMode: _textSelectMode,
    panStart: !!_panStart,
    gestureActive: _gestureActive,
    scale: _scale,
    fitScale: _fitScale,
    hasPdf: !!_pdf,
    scrollClasses: _scroll ? _scroll.className : '(no scroll el)',
    selectionLen: (() => { try { return (window.getSelection()?.toString() || '').length } catch { return -1 } })(),
    clientVectorActive: _clientVectorActive,
    pageVectorClass: _pageVectorClass.get(_clientVectorKey(_currentPageIdx)) || 'unknown',
  })
}

export function setPageFilter(pages) {
  _pageFilter = pages
}

export const getPageFilter = () => _pageFilter

export function filteredPageCount() {
  if (_pageFilter) return _pageFilter.length
  return _pdf ? _pdf.numPages : 0
}

export function filteredIndex() {
  if (!_pageFilter) return _currentPageIdx
  return _pageFilter.indexOf(_currentPageIdx)
}

export async function goToPage(idx) {
  if (!_pdf) return
  const clamped = Math.max(0, Math.min(idx, _pdf.numPages - 1))
  // Skip only when this page is genuinely the one currently RENDERED -- not just
  // the last one we intended to show. A superseded/failed render (or a restored
  // _currentPageIdx) leaves the index pointing at a page that never displayed;
  // trusting the index alone here would permanently lock that page out of
  // navigation ("clicking it does nothing"). Re-centre in the already-rendered case.
  if (clamped === _currentPageIdx && _page && _renderPageIdx === clamped) {
    requestAnimationFrame(() => _centerScrollOnPage())
    return
  }
  const navSerial = ++_navSerial
  _clearGestureState()
  _clearTileLayer()
  _renderSerial += 1
  _currentPageIdx = clamped

  // Streamed PDFs fetch each page's bytes on demand via HTTP range requests; a
  // page whose range fetch fails/stalls would otherwise leave the viewer stuck
  // on the PREVIOUS page (scrollbar moves, page never repaints). Retry the load
  // a few times before giving up so a flaky range never strands the user.
  let page = null
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      page = await _loadPage(clamped)
      if (page) break
    } catch (err) {
      if (navSerial !== _navSerial) return   // a newer navigation superseded us
      console.warn('[viewer] page load failed, retrying', clamped + 1, err)
    }
    if (navSerial !== _navSerial || clamped !== _currentPageIdx) return
    await new Promise(r => setTimeout(r, 200 * (attempt + 1)))
  }
  if (navSerial !== _navSerial || clamped !== _currentPageIdx) return
  if (!page) { console.error('[viewer] page load failed after retries', clamped + 1); return }

  _page = page
  _fitScale = _computeFitScale(page)
  _scale = _fitScale
  _userZoom = 1.0
  _renderScale = _fitScale
  await _renderPage()
  // Self-heal: a concurrent render (resize/zoom/prefetch/rapid nav) or a render
  // error can supersede this one and return WITHOUT painting, leaving the old
  // page on the canvas even though nav state advanced. If we're still the latest
  // navigation but the intended page never painted, repaint once.
  if (navSerial === _navSerial && _renderPageIdx !== clamped) {
    await _renderPage()
  }
  _applyZoomPadding()
  requestAnimationFrame(() => _centerScrollOnPage())
}

export function jumpFirst() { goToPage(0) }
export function jumpLast() { if (_pdf) goToPage(_pdf.numPages - 1) }

export async function zoomIn() { await _zoomStep(ZOOM_STEP) }
export async function zoomOut() { await _zoomStep(1 / ZOOM_STEP) }

export async function zoomFit() {
  if (!_page) return
  _clearGestureState()
  _fitScale = _computeFitScale(_page)
  _scale = _fitScale
  _userZoom = 1.0
  await _renderPage()
  _applyZoomPadding()
  requestAnimationFrame(() => _centerScrollOnPage())
}

export function enterZoomMode() {
  if (!_pdf || _zoomMode) return
  _zoomMode = true
  _userZoom = 1.0
  _scroll.classList.add('zoom-active')
  requestAnimationFrame(() => {
    _applyZoomPadding()
    _centerScrollOnPage()
  })
  _emitter.dispatchEvent(new CustomEvent('zoommodechange', {
    detail: { active: true, scale: _scale, userZoom: _userZoom },
  }))
}

export function exitZoomMode() {
  if (!_zoomMode) return
  _zoomMode = false
  _userZoom = 1.0
  _clearGestureState()
  _scroll.classList.remove('zoom-active')
  _container.style.padding = ''
  _emitter.dispatchEvent(new CustomEvent('zoommodechange', {
    detail: { active: false, scale: _scale, userZoom: 1.0 },
  }))
  _fitScale = _computeFitScale(_page)
  _scale = _fitScale
  _renderPage()
}

export function saveState() {
  return {
    mode: _mode,
    pdf: _pdf,
    page: _page,
    renderedManifest: _renderedManifest,
    currentPageIdx: _currentPageIdx,
    scale: _scale,
    fitScale: _fitScale,
    zoomMode: _zoomMode,
    userZoom: _userZoom,
    scrollLeft: _scroll?.scrollLeft ?? 0,
    scrollTop: _scroll?.scrollTop ?? 0,
  }
}

export async function restoreState(s) {
  _resetDocumentState()
  _mode = s.mode || 'pdf'
  _renderedManifest = s.renderedManifest || null
  _pdf = s.pdf || (_mode === 'rendered' && _renderedManifest
    ? { __rendered: true, numPages: _renderedManifest.page_count || _renderedManifest.pages?.length || 0 }
    : null)
  _currentPageIdx = s.currentPageIdx ?? 0
  _page = s.page || await _loadPage(_currentPageIdx)
  _scale = s.scale
  _fitScale = s.fitScale
  _zoomMode = !!s.zoomMode
  _userZoom = s.userZoom ?? 1.0

  if (_zoomMode) _scroll.classList.add('zoom-active')

  if (_pdf && _page) {
    _scroll.style.cursor = 'crosshair'
    await _renderPage()
    if (_zoomMode) {
      _applyZoomPadding()
      requestAnimationFrame(() => {
        _scroll.scrollLeft = s.scrollLeft ?? 0
        _scroll.scrollTop = s.scrollTop ?? 0
      })
    }
  } else {
    _scroll.style.cursor = 'default'
  }
}

export function detach() {
  if (_renderTask?.cancel) _renderTask.cancel()
  _clearTileLayer()
  _resetDocumentState()
  _scale = 1.0
  _fitScale = 1.0
  if (_textLayerInst) {
    _textLayerInst.cancel()
    _textLayerInst = null
  }
  if (_textLayerDiv) {
    _textLayerDiv.innerHTML = ''
    _textLayerDiv.style.display = ''
  }
  if (_pdfCanvas) {
    _ctx.clearRect(0, 0, _pdfCanvas.width, _pdfCanvas.height)
    _pdfCanvas.width = 0
    _pdfCanvas.height = 0
    _pdfCanvas.style.width = '0'
    _pdfCanvas.style.height = '0'
  }
  if (_wrapper) {
    _wrapper.style.width = '0'
    _wrapper.style.height = '0'
  }
  if (_container) _container.style.padding = ''
  if (_scroll) {
    _scroll.style.cursor = 'default'
    _scroll.scrollLeft = 0
    _scroll.scrollTop = 0
    _scroll.classList.remove('zoom-active')
    _scroll.classList.remove('panning')
  }
}

export function on(event, cb) {
  _emitter.addEventListener(event, cb)
}

export function handleKeydown(e) {
  if (e.target.tagName === 'INPUT') return
  if (e.ctrlKey || e.metaKey) {
    if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomIn() }
    if (e.key === '-') { e.preventDefault(); zoomOut() }
    if (e.key === '0') { e.preventDefault(); zoomFit() }
  }
}

export async function renderRenderedAsset(canvas, pageIdx, widthPx, kind = 'thumb', dpr = 1) {
  if (_mode !== 'rendered' || !_renderedManifest) return false
  const page = _getRenderedPageInfo(pageIdx)
  if (!page || kind !== 'thumb') return false
  const img = await _loadImage(page.thumb_url)
  const sourceW = img.naturalWidth || page.thumb_width || 1
  const sourceH = img.naturalHeight || page.thumb_height || 1
  const scale = (widthPx * dpr) / sourceW
  canvas.width = Math.max(1, Math.round(sourceW * scale))
  canvas.height = Math.max(1, Math.round(sourceH * scale))
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return true
}

// Manifest-explicit thumbnail variant: renders the fast server-side WebP thumbnail even
// when the viewer is in pure-vector 'pdf' mode (cached plans keep _renderedManifest set
// for exactly this — the page-strip thumbs stay instant instead of re-rendering via pdf.js).
export async function renderRenderedThumbFromManifest(canvas, manifest, pageIdx, widthPx, dpr = 1) {
  const page = (manifest?.pages || [])[pageIdx]
  if (!page || !page.thumb_url) return false
  const img = await _loadImage(page.thumb_url)
  const sourceW = img.naturalWidth || page.thumb_width || 1
  const sourceH = img.naturalHeight || page.thumb_height || 1
  const scale = (widthPx * dpr) / sourceW
  canvas.width = Math.max(1, Math.round(sourceW * scale))
  canvas.height = Math.max(1, Math.round(sourceH * scale))
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return true
}

export async function renderRenderedPreviewAsset(canvas, pageIdx, widthPx, dpr = 1) {
  if (_mode !== 'rendered' || !_renderedManifest) return null
  return renderRenderedPreviewAssetFromManifest(canvas, _renderedManifest, pageIdx, widthPx, dpr)
}

// Manifest-explicit variant: works even when the viewer is detached (e.g. callout view).
export async function renderRenderedPreviewAssetFromManifest(canvas, manifest, pageIdx, widthPx, dpr = 1) {
  if (!manifest) return null
  const page = (manifest.pages || [])[pageIdx]
  if (!page) return null
  const img = await _loadRenderedPreviewImage(page, widthPx, dpr)
  if (!img) return null
  const sourceW = img.naturalWidth || page.default_preview_width || 1
  const sourceH = img.naturalHeight || page.default_preview_height || 1
  const scale = (widthPx * dpr) / sourceW
  canvas.width = Math.max(1, Math.round(sourceW * scale))
  canvas.height = Math.max(1, Math.round(sourceH * scale))
  canvas.style.width = `${widthPx}px`
  canvas.style.height = 'auto'
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return {
    k: page.width_pt ? (canvas.width / page.width_pt) * 72 / 150 : 1,
  }
}

export async function renderPdfUrlAsset(canvas, url, pageIdx, widthPx, dpr = 1) {
  if (!url) return null
  let docPromise = _pdfUrlDocCache.get(url)
  if (!docPromise) {
    docPromise = fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Could not load PDF: ${url} (${res.status})`)
        return res.arrayBuffer()
      })
      .then((data) => pdfjsLib.getDocument({ data }).promise)
    _pdfUrlDocCache.set(url, docPromise)
  }
  const doc = await docPromise
  const page = await doc.getPage(pageIdx + 1)
  const vp1 = page.getViewport({ scale: 1 })
  const renderScale = (widthPx * dpr) / vp1.width
  const vp = page.getViewport({ scale: renderScale })
  canvas.width = Math.round(vp.width)
  canvas.height = Math.round(vp.height)
  canvas.style.width = `${widthPx}px`
  canvas.style.height = 'auto'
  await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
  return { k: renderScale * 72 / 150 }
}

async function _openCurrentPage() {
  _navSerial += 1
  _clearGestureState()
  _fitScale = _computeFitScale(_page)
  _scale = _fitScale
  _renderScale = _fitScale
  _userZoom = 1.0
  await _renderPage()
  _scroll.style.cursor = 'crosshair'
  enterZoomMode()
  requestAnimationFrame(() => {
    _applyZoomPadding()
    _centerScrollOnPage()
  })
}

async function _loadPage(pageIdx) {
  if (_mode === 'rendered') return _getRenderedPageInfo(pageIdx)
  return _pdf.getPage(pageIdx + 1)
}

function _getRenderedPageInfo(pageIdx) {
  const pages = _renderedManifest?.pages || []
  return pages[pageIdx] || null
}

function _getViewport(page, scale) {
  if (_mode === 'rendered') {
    return {
      width: (page?.width_pt || 0) * scale,
      height: (page?.height_pt || 0) * scale,
    }
  }
  return page.getViewport({ scale })
}

async function _renderPage(anchor = null) {
  if (!_pdf || !_pdfCanvas || !_page) return
  const renderSerial = ++_renderSerial
  const page = _page
  const pageIdx = _currentPageIdx
  const scale = _scale
  const fitScale = _fitScale

  if (_textLayerInst) {
    _textLayerInst.cancel()
    _textLayerInst = null
  }

  let dpr = Math.max(RENDER_QUALITY, window.devicePixelRatio || 1)
  let viewport = _getViewport(page, scale * dpr)
  let rawW = Math.round(viewport.width)
  let rawH = Math.round(viewport.height)
  const pixelLimit = _mode === 'rendered' ? MAX_RENDERED_CANVAS_PIXELS : MAX_CANVAS_PIXELS
  const dimLimit = _mode === 'rendered' ? MAX_RENDERED_CANVAS_DIM : MAX_CANVAS_DIM

  if (rawW > dimLimit || rawH > dimLimit || rawW * rawH > pixelLimit) {
    const dimRatio = Math.max(rawW / dimLimit, rawH / dimLimit, 1)
    const pixelRatio = Math.sqrt((rawW * rawH) / pixelLimit)
    const reduction = Math.max(dimRatio, pixelRatio)
    dpr = Math.max(0.05, dpr / reduction)
    viewport = _getViewport(page, scale * dpr)
    rawW = Math.round(viewport.width)
    rawH = Math.round(viewport.height)
  }

  const cssW = Math.round(viewport.width / dpr)
  const cssH = Math.round(viewport.height / dpr)
  let renderedImg = null
  let renderedDpr = dpr
  let renderedLayoutApplied = false

  if (_mode === 'rendered') {
    const deviceDpr = window.devicePixelRatio || 1
    // Fetch the preview SUPERSAMPLED (oversample x device px) so we have extra source
    // detail to area-average from when downscaling.
    const fetchDpr = _previewFetchDpr(cssW)
    // Canvas BACKING store = display resolution (device px). The element is shown 1:1
    // (style width = cssW below), so the browser never downscales the canvas element.
    // The supersampled preview is downscaled into this backing via drawImage instead.
    renderedDpr = deviceDpr
    rawW = Math.max(1, Math.round(cssW * deviceDpr))
    rawH = Math.max(1, Math.round(cssH * deviceDpr))
    if (!anchor && _renderPageIdx !== pageIdx) {
      renderedLayoutApplied = _applyRenderedLayout(renderSerial, page, pageIdx, rawW, rawH, cssW, cssH, scale, anchor)
      if (!renderedLayoutApplied) return
      _drawRenderedPlaceholder(renderSerial, page, rawW, rawH)
    }
    renderedImg = await _loadRenderedPreviewImage(page, cssW, fetchDpr)
  }

  if (renderSerial !== _renderSerial || page !== _page || pageIdx !== _currentPageIdx) return

  if (_mode === 'rendered') {
    if (!renderedLayoutApplied) {
      renderedLayoutApplied = _applyRenderedLayout(renderSerial, page, pageIdx, rawW, rawH, cssW, cssH, scale, anchor)
      if (!renderedLayoutApplied) return
    }
  } else {
    // pdf.js path: the display canvas backing is the DISPLAY resolution (device px) shown
    // 1:1 (style width = cssW). pdf.js renders SUPERSAMPLED into an offscreen canvas below
    // and we downscale into this one with a high-quality filter — so the browser never does
    // the cheap canvas-element downscale that muddies text.
    // NOTE: do NOT resize the visible canvas backing store here — assigning
    // _pdfCanvas.width clears it to blank, which flashes black during the async
    // offscreen render below. Only update the CSS size now so the previous frame
    // stretches to the new zoom as an interim preview; the backing store is resized
    // and repainted atomically right before the blit, once the render is ready.
    _wrapper.style.transform = ''
    _wrapper.style.transformOrigin = ''
    _pdfCanvas.style.width = `${cssW}px`
    _pdfCanvas.style.height = `${cssH}px`
    _wrapper.style.width = `${cssW}px`
    _wrapper.style.height = `${cssH}px`

    _renderScale = scale
    _renderCssW = cssW
    _renderCssH = cssH
    _renderPageIdx = pageIdx

    if (_zoomMode) {
      _applyZoomPadding()
      if (anchor) _scrollToAnchor(anchor, scale)
    }
  }

  if (_mode === 'rendered') {
    // Setting canvas.width/height (in _applyRenderedLayout) resets the 2D context and
    // reverts imageSmoothingQuality to the browser default ('low'). Re-assert 'high' here
    // so the supersampled preview is downscaled with a good area-average filter, not the
    // muddy default bilinear.
    _ctx.imageSmoothingEnabled = true
    _ctx.imageSmoothingQuality = 'high'
    _ctx.fillStyle = '#fff'
    _ctx.fillRect(0, 0, rawW, rawH)
    if (renderedImg) {
      _ctx.drawImage(renderedImg, 0, 0, rawW, rawH)
      _maybeSharpen(_ctx, rawW, rawH)
    }
    _updatePreviewDiagnostics(page, cssW, cssH, renderedImg, renderedDpr)
    _prefetchRenderedNeighbors()
    // Feature B: for LIGHT pages, upgrade the raster preview to a crisp client-vector
    // render (and suppress tiles); DENSE pages keep the tile path. Cancel any in-flight
    // vector render from a previous page/scale first.
    if (_clientVectorTask) { try { _clientVectorTask.cancel() } catch { /* ignore */ } _clientVectorTask = null }
    const vkey = _clientVectorKey(pageIdx)
    const vclass = _pageVectorClass.get(vkey)
    // Chrome/PDFium-style: render the REAL vector page (supersampled) as the crisp layer for
    // every page at fit AND moderate zoom. The WebP preview stays as the instant placeholder
    // underneath; the vector render swaps in when ready and suppresses tiles. Hand off to the
    // windowed tile mosaic only at EXTREME zoom (full-page canvas over the size cap) or for
    // pages already known to be pathologically dense.
    const vectorFits = Math.max(_pdfCanvas.width, _pdfCanvas.height) <= CLIENT_VECTOR_MAX_DIM
    if (_clientVectorMaxOps() > 0 && vclass !== 'dense' && vectorFits) {
      _clientVectorActive = true
      _clearRenderedTiles()
      _renderClientVector(renderSerial, pageIdx)
    } else {
      // Load tiles immediately (delay 0) so the crisp mosaic replaces the soft preview
      // as fast as possible — the preview underneath is the instant placeholder.
      _clientVectorActive = false
      _scheduleVisibleTiles(renderSerial, 0)
    }
    _renderRenderedTextLayer(renderSerial, pageIdx, scale)
  } else {
    // Render pdf.js SUPERSAMPLED into an offscreen canvas at the full viewport resolution
    // (rawW x rawH = cssW*RENDER_QUALITY), then downscale into the display canvas (device
    // res) with a high-quality area filter. This matches how native PDF viewers stay crisp
    // and avoids the browser's cheap canvas-element downscale.
    const off = document.createElement('canvas')
    off.width = rawW
    off.height = rawH
    const offCtx = off.getContext('2d', { alpha: false })
    offCtx.fillStyle = '#fff'
    offCtx.fillRect(0, 0, rawW, rawH)
    _renderTask = _page.render({ canvasContext: offCtx, viewport })
    try {
      await _renderTask.promise
    } catch (err) {
      if (err?.name !== 'RenderingCancelledException') console.error('render error', err)
      return
    }
    if (renderSerial !== _renderSerial) return
    _renderTask = null

    // Resize the visible canvas backing store to display resolution and blit the
    // supersampled offscreen render into it — all synchronously, so the canvas is
    // never shown blank (no black flash between clearing and repainting).
    const deviceDpr = window.devicePixelRatio || 1
    const deviceW = Math.max(1, Math.round(cssW * deviceDpr))
    const deviceH = Math.max(1, Math.round(cssH * deviceDpr))
    _pdfCanvas.width = deviceW
    _pdfCanvas.height = deviceH
    _ctx.imageSmoothingEnabled = true
    _ctx.imageSmoothingQuality = 'high'
    _ctx.fillStyle = '#fff'
    _ctx.fillRect(0, 0, deviceW, deviceH)
    _ctx.drawImage(off, 0, 0, deviceW, deviceH)
    // Engineering drawing: crispen ONLY the PDF text regions, never the whole sheet.
    _enhanceVectorText(page, renderSerial, pageIdx, scale, deviceDpr, deviceW, deviceH)

    if (_textLayerDiv) {
      _textLayerDiv.innerHTML = ''
      _textLayerDiv.style.display = ''
      _textLayerDiv.style.setProperty('--scale-factor', scale)
      const textViewport = _getViewport(page, scale)
      _textLayerInst = new TextLayer({
        textContentSource: page.streamTextContent(),
        container: _textLayerDiv,
        viewport: textViewport,
      })
      _textLayerInst.render().catch(err => {
        if (!String(err).toLowerCase().includes('cancel')) {
          console.warn('[textLayer] render error', err)
        }
      })
    }
  }

  _emitter.dispatchEvent(new CustomEvent('pagerender', {
    detail: {
      pageIdx,
      scale: _mode === 'rendered' ? scale * renderedDpr : scale * (window.devicePixelRatio || 1),
      canvas: _pdfCanvas,
      cssWidth: cssW,
      cssHeight: cssH,
      viewport,
    },
  }))
  _emitter.dispatchEvent(new CustomEvent('scalechange', {
    detail: { scale: _mode === 'rendered' ? scale * renderedDpr : scale * (window.devicePixelRatio || 1), fitScale, userZoom: scale / Math.max(fitScale, 0.0001) },
  }))
}

function _applyRenderedLayout(renderSerial, page, pageIdx, rawW, rawH, cssW, cssH, scale, anchor = null) {
  if (renderSerial !== _renderSerial || page !== _page || pageIdx !== _currentPageIdx) return false

  const pageChanged = _renderPageIdx !== pageIdx
  _wrapper.style.transform = ''
  _wrapper.style.transformOrigin = ''
  _pdfCanvas.width = rawW
  _pdfCanvas.height = rawH
  _pdfCanvas.style.width = `${cssW}px`
  _pdfCanvas.style.height = `${cssH}px`
  _wrapper.style.width = `${cssW}px`
  _wrapper.style.height = `${cssH}px`

  _renderScale = scale
  _renderCssW = cssW
  _renderCssH = cssH
  _renderPageIdx = pageIdx

  if (pageChanged) _clearTileLayer()
  _ensureTileLayer()
  _ctx.fillStyle = '#fff'
  _ctx.fillRect(0, 0, rawW, rawH)

  if (_zoomMode) {
    _applyZoomPadding()
    if (anchor) _scrollToAnchor(anchor, scale)
  }

  return true
}

async function _loadRenderedThumbnail(page) {
  if (page?.thumb_url) {
    try {
      return await _loadImage(page.thumb_url)
    } catch (err) {
      console.warn('[viewer] thumbnail fallback failed', err)
    }
  }
  return null
}

async function _loadRenderedPreviewImage(page, cssWidth, dpr) {
  const previewUrl = _renderedPreviewUrl(page, cssWidth, dpr)
  if (previewUrl) {
    try {
      return await _loadImage(previewUrl)
    } catch (err) {
      console.warn('[viewer] preview load failed', err)
    }
  }
  return _loadRenderedThumbnail(page)
}

// Preview fetch supersample factor for a given CSS width. Shared by the live render and the
// neighbour-prefetch so both request the IDENTICAL preview URL (guarantees a cache hit when
// flipping pages). Oversamples (device px x FIT_PREVIEW_OVERSAMPLE), capped to the server's
// preview_max_width.
function _previewFetchDpr(cssW) {
  const previewMaxWidth = Math.max(_renderedManifest?.preview_max_width || 0, 7000)
  const deviceDpr = window.devicePixelRatio || 1
  const desiredPreviewDpr = Math.max(1, deviceDpr * FIT_PREVIEW_OVERSAMPLE)
  return Math.max(0.2, Math.min(desiredPreviewDpr, previewMaxWidth / Math.max(cssW, 1)))
}

function _previewSharpenAmount() {
  try {
    const override = localStorage.getItem('viewerSharpen')
    if (override !== null && override !== '') {
      const v = parseFloat(override)
      if (!Number.isNaN(v)) return _clamp(v, 0, 3)
    }
  } catch (e) { /* localStorage unavailable */ }
  return PREVIEW_SHARPEN_AMOUNT
}

function _textContrastGamma() {
  try {
    const override = localStorage.getItem('viewerTextContrast')
    if (override !== null && override !== '') {
      const v = parseFloat(override)
      if (!Number.isNaN(v) && v > 0) return _clamp(v, 0.3, 1)
    }
  } catch (e) { /* localStorage unavailable */ }
  return PREVIEW_TEXT_GAMMA
}

// 256-entry lookup for the stem-darkening curve: snap a small black/white point then apply
// gamma. Cached and rebuilt only when the gamma changes.
let _textLut = null
let _textLutGamma = -1
function _buildTextLut(gamma) {
  if (_textLut && _textLutGamma === gamma) return _textLut
  const black = 0.03, white = 0.97, span = white - black
  const lut = new Uint8ClampedArray(256)
  for (let v = 0; v < 256; v++) {
    let x = (v / 255 - black) / span
    if (x < 0) x = 0; else if (x > 1) x = 1
    lut[v] = Math.round(Math.pow(x, gamma) * 255)
  }
  _textLut = lut
  _textLutGamma = gamma
  return lut
}

// Reusable readback canvas for the unsharp pass. Flagged willReadFrequently so getImageData
// stays on the fast CPU-backed path — reading directly from the GPU-backed display canvas is
// what triggers the "Multiple readback operations" warning and is slow.
let _sharpenCanvas = null
let _sharpenCtx = null

// In-place unsharp mask (3x3) applied to the just-drawn contents of the given display context.
// Counteracts the gray-fade that thin linework suffers when a supersampled image is downscaled
// into the display canvas. Pixel readback happens on an offscreen willReadFrequently canvas.
function _maybeSharpen(ctx, w, h) {
  const amount = _previewSharpenAmount()
  const gamma = _textContrastGamma()
  const doContrast = gamma < 0.999
  const doSharpen = amount > 0
  if ((!doSharpen && !doContrast) || w < 3 || h < 3 || w * h > PREVIEW_SHARPEN_MAX_PIXELS) return
  const lut = doContrast ? _buildTextLut(gamma) : null
  if (!_sharpenCanvas) {
    _sharpenCanvas = document.createElement('canvas')
    _sharpenCtx = _sharpenCanvas.getContext('2d', { willReadFrequently: true })
  }
  if (!_sharpenCtx) return
  if (_sharpenCanvas.width !== w || _sharpenCanvas.height !== h) {
    _sharpenCanvas.width = w
    _sharpenCanvas.height = h
  }
  let src
  try {
    _sharpenCtx.clearRect(0, 0, w, h)
    _sharpenCtx.drawImage(ctx.canvas, 0, 0)
    src = _sharpenCtx.getImageData(0, 0, w, h)
  } catch (e) {
    return // tainted or oversized canvas
  }
  const s = src.data
  const out = _sharpenCtx.createImageData(w, h)
  const d = out.data
  const rowBytes = w * 4
  const center = 1 + 4 * amount
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * rowBytes + x * 4
      const edge = x === 0 || y === 0 || x === w - 1 || y === h - 1
      if (edge || !doSharpen) {
        // No convolution here — just copy (optionally through the darken curve).
        if (lut) { d[i] = lut[s[i]]; d[i + 1] = lut[s[i + 1]]; d[i + 2] = lut[s[i + 2]] }
        else { d[i] = s[i]; d[i + 1] = s[i + 1]; d[i + 2] = s[i + 2] }
        d[i + 3] = s[i + 3]
        continue
      }
      const up = i - rowBytes, dn = i + rowBytes, lf = i - 4, rt = i + 4
      for (let c = 0; c < 3; c++) {
        let v = center * s[i + c] - amount * (s[up + c] + s[dn + c] + s[lf + c] + s[rt + c])
        v = v < 0 ? 0 : v > 255 ? 255 : v
        d[i + c] = lut ? lut[v | 0] : v
      }
      d[i + 3] = s[i + 3]
    }
  }
  _sharpenCtx.putImageData(out, 0, 0)
  ctx.drawImage(_sharpenCanvas, 0, 0)
}

// ── Text-only crispen (vector render) ─────────────────────────────────────────
// An engineering drawing must NOT be globally sharpened. Instead, pdf.js tells us
// exactly where every text string sits; we darken only the dark pixels inside those
// boxes so small plan annotations read solid at fit, leaving all linework untouched.
// Large text (already crisp) and zoomed-in views are skipped.
async function _pageTextContent(page, pageIdx) {
  const key = `${_renderedManifest?.doc_id || _textPdfDocId || ''}:${pageIdx}`
  let tc = _textContentCache.get(key)
  if (!tc) {
    tc = await page.getTextContent()
    _textContentCache.set(key, tc)
  }
  return tc
}

async function _enhanceVectorText(page, renderSerial, pageIdx, scale, deviceDpr, w, h) {
  // Only worth it while the sheet is rendered small (fit / low zoom); once zoomed in,
  // pdf.js text is already crisp. Guard the readback size so it never hitches.
  if (w < 2 || h < 2 || scale >= 0.8 || w * h > PREVIEW_SHARPEN_MAX_PIXELS) return
  let tc
  try { tc = await _pageTextContent(page, pageIdx) } catch { return }
  if (renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'pdf') return
  const items = tc?.items || []
  if (!items.length) return

  const viewport = page.getViewport({ scale: scale * deviceDpr })
  const pad = Math.max(1, Math.round(2 * deviceDpr))
  const boxes = []
  for (const item of items) {
    if (!item.str || !item.str.trim()) continue
    const tx = pdfjsLib.Util.transform(viewport.transform, item.transform)
    const fontHeight = Math.hypot(tx[2], tx[3])       // glyph height in canvas px
    const fhCss = fontHeight / deviceDpr
    if (fhCss >= 14) continue                          // already crisp on screen
    const gamma = 1.8                                  // treat mid text as small: same bolding
    const width = (item.width || 0) * viewport.scale
    boxes.push({
      x0: Math.max(0, Math.floor(tx[4] - pad)),
      y0: Math.max(0, Math.floor(tx[5] - fontHeight - pad)),   // tx[5] = baseline
      x1: Math.min(w, Math.ceil(tx[4] + width + pad)),
      y1: Math.min(h, Math.ceil(tx[5] + fontHeight * 0.3 + pad)),
      gamma,
    })
  }
  if (!boxes.length || renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'pdf') return

  let img
  try { img = _ctx.getImageData(0, 0, w, h) } catch { return }
  const d = img.data
  const seen = new Uint8Array(w * h)   // darken each pixel at most once (overlapping boxes)
  for (const b of boxes) {
    if (b.x1 <= b.x0 || b.y1 <= b.y0) continue
    const lut = _darkenLutFor(b.gamma)
    for (let yy = b.y0; yy < b.y1; yy++) {
      let p = yy * w + b.x0
      let i = p * 4
      for (let xx = b.x0; xx < b.x1; xx++, p++, i += 4) {
        if (seen[p]) continue
        seen[p] = 1
        // Skip white background (the curve leaves it untouched anyway); darken the rest
        // through the smooth gamma LUT so strokes bolden without hard/blocky edges.
        if (d[i] >= 250 && d[i + 1] >= 250 && d[i + 2] >= 250) continue
        d[i] = lut[d[i]]; d[i + 1] = lut[d[i + 1]]; d[i + 2] = lut[d[i + 2]]
      }
    }
  }
  if (renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'pdf') return
  _ctx.putImageData(img, 0, 0)
}

// Lazily load (and cache) the original PDF via pdf.js for text extraction in rendered
// mode. Uses a URL source so pdf.js fetches structure/pages via HTTP range requests
// instead of downloading the whole file up front.
function _ensureTextPdf() {
  if (_mode !== 'rendered') return Promise.resolve(null)
  const docId = _renderedManifest?.doc_id
  if (!docId) return Promise.resolve(null)
  if (_textPdfDocId !== docId || !_textPdfPromise) {
    _textPdfDocId = docId
    // pdf.js issues its own HTTP range requests using an ABSOLUTE URL, which
    // bypasses the app's global fetch wrapper (it only auth-injects on paths
    // starting with '/api'). Attach the Clerk bearer token explicitly via
    // httpHeaders so the protected /file endpoint doesn't 401.
    _textPdfPromise = (async () => {
      const token = await getToken()
      const params = { url: documentPdfUrl(docId) }
      if (token) params.httpHeaders = { Authorization: `Bearer ${token}` }
      return pdfjsLib.getDocument(params).promise
    })().catch((err) => {
      console.warn('[textLayer] background PDF load failed', err)
      _textPdfPromise = null
      return null
    })
  }
  return _textPdfPromise
}

// ── Feature B: hybrid client-vector render for light pages ────────────────────

function _clientVectorKey(pageIdx) {
  return `${_renderedManifest?.doc_id || _textPdfDocId || ''}:${pageIdx}`
}

function _clientVectorMaxOps() {
  try {
    const override = localStorage.getItem('viewerVectorOps')
    if (override !== null && override !== '') {
      const v = parseInt(override, 10)
      if (!Number.isNaN(v)) return v
    }
  } catch (e) { /* localStorage unavailable */ }
  return CLIENT_VECTOR_MAX_OPS
}

function _clientVectorSupersample() {
  try {
    const override = localStorage.getItem('viewerVectorSS')
    if (override !== null && override !== '') {
      const v = parseFloat(override)
      if (!Number.isNaN(v) && v >= 1) return Math.min(v, 4)
    }
  } catch (e) { /* localStorage unavailable */ }
  return CLIENT_VECTOR_SS
}

function _clearRenderedTiles() {
  if (_tileLayer) _tileLayer.innerHTML = ''
}

// Fall back to the windowed raster tile mosaic (used when a page is pathologically dense or
// the vector PDF can't be loaded). Only acts if we're still on the same page/serial.
function _clientVectorFallbackToTiles(renderSerial, pageIdx) {
  if (renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'rendered') return
  _clientVectorActive = false
  _scheduleVisibleTiles(renderSerial, 0)
}

// Render the real PDF page client-side via pdf.js, SUPERSAMPLED, and downscale into the
// display canvas — crisp, resolution-independent linework and text with no tile fetch, the
// way the native Chrome/PDFium viewer stays sharp. Classifies density once (cached): pages
// over the op ceiling are handed to the tile mosaic instead (rendering them every frame would
// jank the browser). Guarded by renderSerial/pageIdx so late results from a prior page drop.
async function _renderClientVector(renderSerial, pageIdx) {
  const doc = await _ensureTextPdf()
  if (!doc || renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'rendered') {
    _clientVectorFallbackToTiles(renderSerial, pageIdx)
    return
  }
  let tpage
  try {
    tpage = await doc.getPage(pageIdx + 1)
  } catch (e) {
    _clientVectorFallbackToTiles(renderSerial, pageIdx)
    return
  }
  if (renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'rendered') return
  // Density classification (once, cached). Pathologically dense pages fall back to tiles.
  const vkey = _clientVectorKey(pageIdx)
  let vclass = _pageVectorClass.get(vkey)
  if (vclass === undefined) {
    try {
      const opList = await tpage.getOperatorList()
      vclass = opList.fnArray.length <= _clientVectorMaxOps() ? 'light' : 'dense'
    } catch (e) {
      vclass = 'dense'
    }
    _pageVectorClass.set(vkey, vclass)
    if (renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'rendered') return
  }
  if (vclass === 'dense') {
    _clientVectorFallbackToTiles(renderSerial, pageIdx)
    return
  }
  const w = _pdfCanvas.width
  const h = _pdfCanvas.height
  if (w < 1 || h < 1) return
  // Supersample: render at up to SS x display res (capped so the offscreen longest side
  // stays under CLIENT_VECTOR_MAX_DIM), then high-quality area-downscale into the display
  // canvas for crisp anti-aliased linework.
  const ssCap = CLIENT_VECTOR_MAX_DIM / Math.max(w, h)
  const ss = Math.max(1, Math.min(_clientVectorSupersample(), ssCap))
  const renderW = Math.max(1, Math.round(w * ss))
  const renderH = Math.max(1, Math.round(h * ss))
  const baseVp = tpage.getViewport({ scale: 1 })
  const vpScale = renderW / Math.max(baseVp.width, 1)
  const viewport = tpage.getViewport({ scale: vpScale })
  const off = document.createElement('canvas')
  off.width = renderW
  off.height = renderH
  const offCtx = off.getContext('2d', { alpha: false })
  offCtx.fillStyle = '#fff'
  offCtx.fillRect(0, 0, renderW, renderH)
  const task = tpage.render({ canvasContext: offCtx, viewport })
  _clientVectorTask = task
  try {
    await task.promise
  } catch (e) {
    return // cancelled or failed — leave the raster preview in place
  }
  if (task !== _clientVectorTask) return
  _clientVectorTask = null
  if (renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'rendered') return
  if (_pdfCanvas.width !== w || _pdfCanvas.height !== h) return // canvas resized under us
  _ctx.imageSmoothingEnabled = true
  _ctx.imageSmoothingQuality = 'high'
  _ctx.drawImage(off, 0, 0, w, h)
  // Snap thin linework/text to full contrast. At fit the whole sheet is only ~30 px/in so
  // 1px lines area-average to gray after the supersample downscale; a mild unsharp pass
  // makes them pop (crisper than Chrome's un-sharpened render). Tunable via localStorage
  // 'viewerSharpen' (0 disables); skipped automatically on huge high-zoom canvases.
  _maybeSharpen(_ctx, w, h)
  _clearRenderedTiles()
}

// Build the selectable vector text layer over the raster preview in rendered mode.
// Fire-and-forget: hides any stale text immediately, then shows the fresh layer once
// the page's text stream is ready (guarded by renderSerial/page so late results drop).
async function _renderRenderedTextLayer(renderSerial, pageIdx, scale) {
  if (!_textLayerDiv) return
  if (_textLayerInst) { _textLayerInst.cancel(); _textLayerInst = null }
  _textLayerDiv.innerHTML = ''
  _textLayerDiv.style.display = 'none'
  let doc
  try {
    doc = await _ensureTextPdf()
  } catch (err) {
    return
  }
  if (!doc || renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'rendered') return
  let tpage
  try {
    tpage = await doc.getPage(pageIdx + 1)
  } catch (err) {
    return
  }
  if (renderSerial !== _renderSerial || pageIdx !== _currentPageIdx || _mode !== 'rendered') return
  _textLayerDiv.style.display = ''
  _textLayerDiv.style.setProperty('--scale-factor', scale)
  const inst = new TextLayer({
    textContentSource: tpage.streamTextContent(),
    container: _textLayerDiv,
    viewport: tpage.getViewport({ scale }),
  })
  _textLayerInst = inst
  inst.render().catch((err) => {
    if (!String(err).toLowerCase().includes('cancel')) {
      console.warn('[textLayer] rendered-mode render error', err)
    }
  })
}

function _drawRenderedPlaceholder(renderSerial, page, rawW, rawH) {
  _loadRenderedThumbnail(page)
    .then((img) => {
      if (!img || renderSerial !== _renderSerial || page !== _page) return
      _ctx.fillStyle = '#fff'
      _ctx.fillRect(0, 0, rawW, rawH)
      _ctx.drawImage(img, 0, 0, rawW, rawH)
    })
    .catch(() => {})
}

async function _zoomStep(factor) {
  if (!_zoomMode) enterZoomMode()
  const anchor = _viewportCenterAnchor()
  _clearGestureState()
  _scale = _clamp(_scale * factor, MIN_SCALE, MAX_SCALE)
  _userZoom = _scale / _fitScale
  await _renderPage(anchor)
  _applyZoomPadding()
}

function _onWheel(e) {
  if (!_pdf) return
  // Scroll always means zoom in this viewer; re-enter zoom mode if it was
  // exited (e.g. via a Fit reset) so the wheel is never stranded.
  if (!_zoomMode) enterZoomMode()
  e.preventDefault()

  const factor = Math.pow(WHEEL_BASE, -e.deltaY / 120)
  const newScale = _clamp(_scale * factor, MIN_SCALE, MAX_SCALE)
  if (Math.abs(newScale - _scale) < 0.0001) return

  if (!_gestureActive) {
    _renderSerial += 1
    _gestureActive = true
    _gestureAnchor = _clientPointAnchor(e.clientX, e.clientY)
    if (!_gestureAnchor) return
    if (_mode !== 'rendered') {
      _wrapper.style.transformOrigin = `${_gestureAnchor.pageX * _renderScale}px ${_gestureAnchor.pageY * _renderScale}px`
    }
  }

  _scale = newScale
  _userZoom = _scale / _fitScale
  if (_mode === 'rendered') {
    _applyTransientRenderedZoom(_gestureAnchor)
  } else {
    _wrapper.style.transform = `scale(${_scale / Math.max(_renderScale, 0.0001)})`
  }

  _emitter.dispatchEvent(new CustomEvent('scalechange', {
    detail: { scale: _scale, fitScale: _fitScale, userZoom: _userZoom },
  }))

  clearTimeout(_renderTimeout)
  _renderTimeout = setTimeout(() => {
    _renderTimeout = null
    _gestureActive = false
    _emitter.dispatchEvent(new CustomEvent('prerender'))
    _renderPage(_gestureAnchor)
  }, 60)
}

function _applyTransientRenderedZoom(anchor) {
  if (!_page || !_wrapper || !_pdfCanvas || !_tileLayer || !anchor) return
  // Hide the vector text during the zoom gesture: its spans are positioned for the
  // settled scale and would sit misaligned while the wrapper is resized live. The
  // debounced _renderPage re-renders the text layer at the new scale on settle.
  if (_textLayerDiv) _textLayerDiv.style.display = 'none'
  const cssW = Math.max(1, Math.round((_page.width_pt || 1) * _scale))
  const cssH = Math.max(1, Math.round((_page.height_pt || 1) * _scale))
  _wrapper.style.transform = ''
  _wrapper.style.transformOrigin = ''
  _pdfCanvas.style.width = `${cssW}px`
  _pdfCanvas.style.height = `${cssH}px`
  _wrapper.style.width = `${cssW}px`
  _wrapper.style.height = `${cssH}px`
  _tileLayer.style.width = `${cssW}px`
  _tileLayer.style.height = `${cssH}px`
  _renderCssW = cssW
  _renderCssH = cssH
  _renderScale = _scale
  _scaleRenderedTiles(_scale)
  _applyZoomPadding()
  _scrollToAnchor(anchor, _scale)
}

function _onScroll() {
  if (_mode !== 'rendered' || !_zoomMode || !_tileLayer) return
  _scheduleVisibleTiles(_renderSerial, TILE_IDLE_MS)
}

function _onMouseDown(e) {
  if (e.button === 1 && _zoomMode) {
    e.preventDefault()
    _panStart = { x: e.clientX, y: e.clientY, sl: _scroll.scrollLeft, st: _scroll.scrollTop, middle: true }
    _scroll.classList.add('panning')
    return
  }

  if (e.button === 0 && _zoomMode && !_toolActive) {
    if (_textSelectMode) {
      const tgt = e.target
      const onTextSpan = tgt &&
        (tgt.tagName === 'SPAN' || tgt.tagName === 'BR') &&
        tgt.closest && tgt.closest('.textLayer')
      if (onTextSpan) return
      // Panning starts on empty area: drop any existing selection so its
      // highlight doesn't jump/repaint on every scroll frame during the drag.
      try { window.getSelection()?.removeAllRanges() } catch { /* ignore */ }
    }
    e.preventDefault()
    _panStart = { x: e.clientX, y: e.clientY, sl: _scroll.scrollLeft, st: _scroll.scrollTop }
    _scroll.classList.add('panning')
  }
}

function _onMouseMove(e) {
  if (!_panStart) return
  _scroll.scrollLeft = _panStart.sl - (e.clientX - _panStart.x)
  _scroll.scrollTop = _panStart.st - (e.clientY - _panStart.y)
}

function _onMouseUp(e) {
  if (!_panStart) return
  if ((e.button === 0 && !_panStart.middle) || (e.button === 1 && _panStart.middle)) {
    _panStart = null
    _scroll.classList.remove('panning')
  }
}

function _onResize() {
  if (!_page) return
  clearTimeout(_resizeTimeout)
  _resizeTimeout = setTimeout(() => {
    _fitScale = _computeFitScale(_page)
    _scale = _fitScale
    _userZoom = 1.0
    _renderPage()
    _applyZoomPadding()
    requestAnimationFrame(() => _centerScrollOnPage())
  }, 200)
}

function _applyZoomPadding() {
  if (!_container || !_scroll || !_wrapper) return
  const pw = parseFloat(_wrapper.style.width) || 800
  const ph = parseFloat(_wrapper.style.height) || 600
  const vw = _scroll.clientWidth || 800
  const vh = _scroll.clientHeight || 600
  _container.style.padding = `${Math.max(ph, vh)}px ${Math.max(pw, vw)}px`
}

function _computeFitScale(page) {
  if (!page || !_scroll) return 1.0
  const GAP = 24
  let cw = _scroll.clientWidth - GAP
  let ch = _scroll.clientHeight - GAP
  if (cw <= 0) cw = window.innerWidth - GAP
  if (ch <= 0) ch = window.innerHeight - GAP
  if (cw <= 0) cw = 600
  if (ch <= 0) ch = 400
  const vp = _getViewport(page, 1)
  const fitW = cw / vp.width
  const fitH = ch / vp.height
  // Acrobat-style "fit width": landscape sheets fill the full viewer width (zero side
  // gutters, ~25% bigger/crisper) and scroll vertically. Portrait sheets fall back to
  // whole-page fit so the zoom doesn't blow up.
  const scale = vp.width >= vp.height ? fitW : Math.min(fitW, fitH)
  return Math.max(MIN_SCALE, scale)
}

function _centerScrollOnPage() {
  if (!_scroll || !_wrapper || !_container) return
  const pw = _wrapper.offsetWidth
  const ph = _wrapper.offsetHeight
  const vw = _scroll.clientWidth
  const vh = _scroll.clientHeight
  const wLeft = _wrapper.offsetLeft + _container.offsetLeft
  const wTop = _wrapper.offsetTop + _container.offsetTop
  _scroll.scrollLeft = wLeft + pw / 2 - vw / 2
  _scroll.scrollTop = wTop + ph / 2 - vh / 2
}

function _viewportCenterAnchor() {
  if (!_scroll) return null
  const sr = _scroll.getBoundingClientRect()
  return _clientPointAnchor(sr.left + sr.width / 2, sr.top + sr.height / 2)
}

function _clientPointAnchor(clientX, clientY) {
  if (!_scroll || !_wrapper || !_container) return null
  const sr = _scroll.getBoundingClientRect()
  const viewX = clientX - sr.left
  const viewY = clientY - sr.top
  const origin = _pageOriginInScroll()
  const cssScale = _currentCssScale()
  const pageW = _page?.width_pt || _renderCssW / cssScale || 1
  const pageH = _page?.height_pt || _renderCssH / cssScale || 1
  return {
    pageX: _clamp((_scroll.scrollLeft + viewX - origin.left) / cssScale, 0, pageW),
    pageY: _clamp((_scroll.scrollTop + viewY - origin.top) / cssScale, 0, pageH),
    viewX,
    viewY,
  }
}

function _scrollToAnchor(anchor, scale) {
  if (!_scroll || !_wrapper || !_container || !anchor) return
  const origin = _pageOriginInScroll()
  _scroll.scrollLeft = origin.left + anchor.pageX * scale - anchor.viewX
  _scroll.scrollTop = origin.top + anchor.pageY * scale - anchor.viewY
}

function _pageOriginInScroll() {
  return {
    left: (_container?.offsetLeft || 0) + (_wrapper?.offsetLeft || 0),
    top: (_container?.offsetTop || 0) + (_wrapper?.offsetTop || 0),
  }
}

function _currentCssScale() {
  if (_mode === 'rendered' && _page?.width_pt) {
    const cssW = parseFloat(_wrapper?.style.width) || _renderCssW || 1
    return Math.max(cssW / _page.width_pt, 0.0001)
  }
  return Math.max(_renderScale, 0.0001)
}

async function _loadImage(url) {
  if (_imageCache.has(url)) return _imageCache.get(url)
  const pending = new Promise((resolve, reject) => {
    const img = new Image()
    img.decoding = 'async'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Could not load image: ${url}`))
    if (url.startsWith('/api/')) {
      let responseHeaders = {}
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`Could not load image: ${url} (${res.status})`)
          responseHeaders = Object.fromEntries(res.headers.entries())
          return res.blob()
        })
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob)
          _imageObjectUrls.set(url, objectUrl)
          img._viewerHeaders = responseHeaders
          img._viewerSourceUrl = url
          img.src = objectUrl
          if (img.decode) img.decode().then(() => resolve(img)).catch(() => {})
        })
        .catch(reject)
    } else {
      img._viewerSourceUrl = url
      img.src = url
      if (img.decode) {
        img.decode().then(() => resolve(img)).catch(() => {})
      }
    }
  })
  _imageCache.set(url, pending)
  try {
    const img = await pending
    _trimRenderedImageCache(url)
    return img
  } catch (err) {
    _imageCache.delete(url)
    throw err
  }
}

function _renderedPreviewUrl(page, cssWidth, dpr) {
  const url = page?.preview_url
  if (!url || !url.includes('/preview')) return url
  const sep = url.includes('?') ? '&' : '?'
  const previewMaxWidth = Math.max(_renderedManifest?.preview_max_width || 0, 7000)
  const maxRequestWidth = Math.min(MAX_PREVIEW_REQUEST_WIDTH, previewMaxWidth)
  const targetPixels = Math.round((cssWidth || 0) * Math.max(0.2, dpr))
  const requestWidth = _clamp(targetPixels, MIN_PREVIEW_REQUEST_WIDTH, maxRequestWidth)
  return `${url}${sep}w=${requestWidth}&dpr=1.00`
}

function _updatePreviewDiagnostics(page, cssWidth, cssHeight, img, renderedDpr) {
  if (_mode !== 'rendered' || !page) return
  const naturalW = img?.naturalWidth || 0
  const naturalH = img?.naturalHeight || 0
  const deviceDpr = window.devicePixelRatio || 1
  const effectiveRatio = naturalW / Math.max(cssWidth || 1, 1)
  const headers = img?._viewerHeaders || {}
  const requested = Number(headers['x-viewer-preview-requested-width'] || 0)
  const bucketed = Number(headers['x-viewer-preview-bucketed-width'] || 0)
  const clamped = headers['x-viewer-preview-clamped'] === '1'
  _fitTilesNeeded = !!img && (clamped || effectiveRatio < deviceDpr * MIN_PREVIEW_EFFECTIVE_RATIO)
  _lastPreviewDiagnostics = {
    page: `${Math.round(page.width_pt || 0)} x ${Math.round(page.height_pt || 0)} pt`,
    display: `${Math.round(cssWidth || 0)} x ${Math.round(cssHeight || 0)} CSS px`,
    devicePixelRatio: Number(deviceDpr.toFixed(2)),
    requestedPreviewWidth: requested || Math.round((cssWidth || 0) * renderedDpr),
    bucketedPreviewWidth: bucketed || null,
    actualPreview: `${naturalW} x ${naturalH}`,
    effectivePreviewRatio: Number(effectiveRatio.toFixed(2)),
    previewFormat: _renderedManifest?.preview_format || headers['x-viewer-preview-format'] || 'unknown',
    previewEncodingMode: _previewEncodingMode(headers),
    currentTileLevel: _chooseTileLevel(),
    visibleTileCount: _visibleTileKeys(_chooseTileLevel()).length,
    loadedTileCount: _tileLayer ? _tileLayer.querySelectorAll('.render-tile.loaded').length : 0,
    previewClamped: clamped,
    fitTilesNeeded: _fitTilesNeeded,
  }
  if (location.hostname === 'localhost' || localStorage.getItem('viewerDiagnostics') === '1') {
    console.debug('[viewer diagnostics]', _lastPreviewDiagnostics)
  }
}

function _previewEncodingMode(headers = {}) {
  const fmt = _renderedManifest?.preview_format || headers['x-viewer-preview-format'] || 'unknown'
  if (fmt !== 'webp') return fmt
  const lossless = _renderedManifest?.preview_webp_lossless ?? headers['x-viewer-preview-webp-lossless'] === '1'
  const nearLossless = _renderedManifest?.preview_webp_near_lossless || headers['x-viewer-preview-webp-near-lossless']
  if (lossless) return `webp lossless near=${nearLossless || 100}`
  return `webp lossy q=${nearLossless || 'default'}`
}

function _tileUrl(page, level, x, y) {
  const template = page?.tile_url_template
  if (!template) return null
  return template
    .replace('{level}', encodeURIComponent(level))
    .replace('{x}', encodeURIComponent(x))
    .replace('{y}', encodeURIComponent(y))
}

function _chooseTileLevel() {
  const levels = (_renderedManifest?.tile_levels || [1, 2, 3])
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b)
  const dpr = window.devicePixelRatio || 1
  const desired = _scale * dpr
  return levels.find(level => level >= desired) || levels[levels.length - 1] || 1
}

function _scheduleVisibleTiles(renderSerial = _renderSerial, delay = TILE_IDLE_MS) {
  if (_mode !== 'rendered' || !_page || !_tileLayer) return
  if (_clientVectorActive) return
  // Tiles load at FIT too (not just zoom): the single WebP preview is lower effective
  // resolution than the level-1 tile mosaic, so fit-view looked soft until tiles were
  // present. The preview stays underneath as the instant placeholder until tiles arrive.
  const serial = ++_tileSerial
  clearTimeout(_tileTimer)
  _tileTimer = setTimeout(() => {
    if (serial !== _tileSerial || renderSerial !== _renderSerial) return
    _renderVisibleTiles(renderSerial)
  }, delay)
}

function _renderVisibleTiles(renderSerial) {
  if (_mode !== 'rendered' || !_page || !_tileLayer || !_scroll || !_wrapper) return

  const tileSize = _renderedManifest?.tile_size || 512
  const level = _chooseTileLevel()
  const layerKey = `${_currentPageIdx}:${level}`
  _tileLayerKey = layerKey
  _scaleRenderedTiles(_scale)

  _tileLayer.style.width = `${_renderCssW}px`
  _tileLayer.style.height = `${_renderCssH}px`

  const tiles = _visibleTileKeys(level)
  const pageWpt = _page.width_pt || 1
  const pageHpt = _page.height_pt || 1
  const wanted = new Set()
  for (const tile of tiles) {
    const key = `${level}:${tile.x}:${tile.y}`
    wanted.add(key)
    if (_tileLayer.querySelector(`[data-tile-key="${key}"]`)) continue
    _appendTile(renderSerial, key, level, tile.x, tile.y, tileSize, pageWpt, pageHpt, _scale)
  }
  _pruneObsoleteTiles(level, wanted)
}

function _visibleTileKeys(level) {
  if (!_page || !_scroll || !_wrapper) return []
  const tileSize = _renderedManifest?.tile_size || 512
  const scrollRect = _scroll.getBoundingClientRect()
  const wrapperRect = _wrapper.getBoundingClientRect()
  const leftCss = Math.max(0, scrollRect.left - wrapperRect.left)
  const topCss = Math.max(0, scrollRect.top - wrapperRect.top)
  const rightCss = Math.min(_renderCssW, scrollRect.right - wrapperRect.left)
  const bottomCss = Math.min(_renderCssH, scrollRect.bottom - wrapperRect.top)
  if (rightCss <= leftCss || bottomCss <= topCss) return []

  const pageWpt = _page.width_pt || 1
  const pageHpt = _page.height_pt || 1
  const tilesX = Math.max(1, Math.ceil(Math.ceil(pageWpt * level) / tileSize))
  const tilesY = Math.max(1, Math.ceil(Math.ceil(pageHpt * level) / tileSize))
  const visibleMinX = Math.max(0, Math.floor((leftCss / _scale) * level / tileSize))
  const visibleMinY = Math.max(0, Math.floor((topCss / _scale) * level / tileSize))
  const visibleMaxX = Math.min(tilesX - 1, Math.ceil(((rightCss / _scale) * level) / tileSize) - 1)
  const visibleMaxY = Math.min(tilesY - 1, Math.ceil(((bottomCss / _scale) * level) / tileSize) - 1)
  if (visibleMaxX < visibleMinX || visibleMaxY < visibleMinY) return []
  const minX = Math.max(0, visibleMinX - 1)
  const minY = Math.max(0, visibleMinY - 1)
  const maxX = Math.min(tilesX - 1, visibleMaxX + 1)
  const maxY = Math.min(tilesY - 1, visibleMaxY + 1)
  const centerX = (visibleMinX + visibleMaxX) / 2
  const centerY = (visibleMinY + visibleMaxY) / 2
  const tiles = []
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      tiles.push({ x, y, distance: Math.hypot(x - centerX, y - centerY) })
    }
  }
  tiles.sort((a, b) => a.distance - b.distance)
  return tiles
}

function _pruneObsoleteTiles(level, wanted) {
  if (!_tileLayer) return
  const currentReady = [...wanted].every(key => _tileLayer.querySelector(`[data-tile-key="${key}"]`))
  for (const img of _tileLayer.querySelectorAll('.render-tile')) {
    const key = img.dataset.tileKey || ''
    const tileLevel = Number(img.dataset.tileLevel || 0)
    if (tileLevel === level && (wanted.has(key) || !currentReady)) continue
    img.remove()
  }
}

function _appendTile(renderSerial, key, level, x, y, tileSize, pageWpt, pageHpt, scale) {
  const url = _tileUrl(_page, level, x, y)
  if (!url || !_tileLayer) return

  const tilePtX = x * tileSize / level
  const tilePtY = y * tileSize / level
  const tilePtW = Math.min(tileSize / level, pageWpt - tilePtX)
  const tilePtH = Math.min(tileSize / level, pageHpt - tilePtY)

  _loadImage(url)
    .then((loaded) => {
      if (renderSerial !== _renderSerial || !_tileLayer) return
      if (_tileLayer.querySelector(`[data-tile-key="${key}"]`)) return
      const img = document.createElement('img')
      img.className = 'render-tile loaded'
      img.dataset.tileKey = key
      img.dataset.tileLevel = String(level)
      img.dataset.tilePtX = String(tilePtX)
      img.dataset.tilePtY = String(tilePtY)
      img.dataset.tilePtW = String(tilePtW)
      img.dataset.tilePtH = String(tilePtH)
      Object.assign(img.style, {
        left: `${tilePtX * scale}px`,
        top: `${tilePtY * scale}px`,
        width: `${tilePtW * scale}px`,
        height: `${tilePtH * scale}px`,
      })
      img.src = loaded.src
      _tileLayer.appendChild(img)
    })
    .catch(() => {})
}

function _scaleRenderedTiles(scale) {
  if (!_tileLayer) return
  for (const img of _tileLayer.querySelectorAll('.render-tile')) {
    const tilePtX = Number(img.dataset.tilePtX || 0)
    const tilePtY = Number(img.dataset.tilePtY || 0)
    const tilePtW = Number(img.dataset.tilePtW || 0)
    const tilePtH = Number(img.dataset.tilePtH || 0)
    Object.assign(img.style, {
      left: `${tilePtX * scale}px`,
      top: `${tilePtY * scale}px`,
      width: `${tilePtW * scale}px`,
      height: `${tilePtH * scale}px`,
    })
  }
}

function _clearTileLayer() {
  clearTimeout(_tileTimer)
  _tileTimer = null
  _tileSerial += 1
  _tileLayerKey = ''
  _fitTilesNeeded = false
  _lastPreviewDiagnostics = null
  if (_tileLayer) {
    _tileLayer.innerHTML = ''
    _tileLayer.style.width = '0'
    _tileLayer.style.height = '0'
  }
}

function _prefetchRenderedNeighbors() {
  if (_mode !== 'rendered' || !_renderedManifest) return
  const pageIdx = _currentPageIdx
  const serial = ++_prefetchSerial
  clearTimeout(_prefetchTimer)
  _prefetchTimer = setTimeout(() => {
    if (serial !== _prefetchSerial || _mode !== 'rendered' || pageIdx !== _currentPageIdx) return
    const pages = _renderedManifest?.pages || []
    const cssW = _renderCssW || 1200
    const fetchDpr = _previewFetchDpr(cssW)
    // Warm a window of neighbours in BOTH directions using the SAME preview URL the
    // renderer will request, so flipping to an adjacent page is instant (no buffer).
    // Forward is weighted heavier (people page forward more). Nearest first.
    const offsets = [1, -1, 2, 3, -2, 4]
    for (const off of offsets) {
      const idx = pageIdx + off
      if (idx < 0 || idx >= pages.length) continue
      const url = _renderedPreviewUrl(pages[idx], cssW, fetchDpr)
      if (!url || _imageCache.has(url)) continue
      _loadImage(url).catch(() => {})
    }
  }, 120)
}

function _trimRenderedImageCache(keepUrl) {
  const previewUrls = [..._imageCache.keys()].filter(url => (
    url.includes('/viewer/pages/') && url.includes('/preview')
  ))
  let removeCount = Math.max(0, previewUrls.length - MAX_PREVIEW_IMAGE_CACHE_ITEMS)
  for (const url of previewUrls) {
    if (removeCount <= 0) break
    if (url === keepUrl) continue
    _imageCache.delete(url)
    _revokeImageObjectUrl(url)
    removeCount -= 1
  }

  const tileUrls = [..._imageCache.keys()].filter(url => (
    url.includes('/viewer/pages/') && url.includes('/tiles/')
  ))
  removeCount = Math.max(0, tileUrls.length - MAX_TILE_IMAGE_CACHE_ITEMS)
  for (const url of tileUrls) {
    if (removeCount <= 0) break
    if (url === keepUrl) continue
    _imageCache.delete(url)
    _revokeImageObjectUrl(url)
    removeCount -= 1
  }
}

function _revokeImageObjectUrl(url) {
  const objectUrl = _imageObjectUrls.get(url)
  if (!objectUrl) return
  URL.revokeObjectURL(objectUrl)
  _imageObjectUrls.delete(url)
}

function _clearGestureState() {
  _panStart = null
  _gestureActive = false
  _gestureAnchor = null
  _wrapper.style.transform = ''
  _wrapper.style.transformOrigin = ''
  clearTimeout(_renderTimeout)
  _renderTimeout = null
}

function _resetDocumentState() {
  if (_renderTask?.cancel) _renderTask.cancel()
  _renderTask = null
  _renderSerial += 1
  _navSerial += 1
  _clearTileLayer()
  clearTimeout(_prefetchTimer)
  _prefetchTimer = null
  _prefetchSerial += 1
  if (_sourceObjectUrl) {
    URL.revokeObjectURL(_sourceObjectUrl)
    _sourceObjectUrl = null
  }
  _imageCache.clear()
  for (const docPromise of _pdfUrlDocCache.values()) {
    docPromise.then((doc) => doc.destroy?.()).catch(() => {})
  }
  _pdfUrlDocCache.clear()
  if (_textPdfPromise) {
    _textPdfPromise.then((doc) => doc?.destroy?.()).catch(() => {})
  }
  _textPdfPromise = null
  _textPdfDocId = null
  if (_clientVectorTask) { try { _clientVectorTask.cancel() } catch { /* ignore */ } _clientVectorTask = null }
  _clientVectorActive = false
  _pageVectorClass.clear()
  _textContentCache.clear()
  for (const objectUrl of _imageObjectUrls.values()) URL.revokeObjectURL(objectUrl)
  _imageObjectUrls.clear()
  _pdf = null
  _page = null
  _renderedManifest = null
  _currentPageIdx = 0
  _navSerial = 0
  _lastSource = null
  _scale = 1.0
  _fitScale = 1.0
  _mode = 'pdf'
  _zoomMode = false
  _userZoom = 1.0
  _renderScale = 1.0
  _renderCssW = 0
  _renderCssH = 0
  _renderPageIdx = -1
  _clearGestureState()
  if (_container) _container.style.padding = ''
  if (_scroll) {
    _scroll.classList.remove('zoom-active')
    _scroll.classList.remove('panning')
  }
}

function _clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v))
}
